/**
 * MeddpicScorecard — MEDDICC scoring widget with visual bars and one-click copy.
 *
 * Displays all 7 MEDDICC dimensions with:
 * - Score bars (0-100)
 * - Status indicators (✓ green, ⚠ yellow, ✗ red)
 * - Notes for each dimension
 * - Champion/Economic Buyer contact info
 * - One-click copy of full scorecard for Salesforce
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp, User, FileText, GitBranch, AlertTriangle,
  Star, Swords, Pencil, Save, X, Loader2
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import CopyButton from './CopyButton';
import './MeddpicScorecard.css';

const DIMENSIONS = [
  { key: 'metrics', dbKey: 'metrics_score', label: 'Metrics', icon: TrendingUp, description: 'Quantified business case (ROI, savings, time)' },
  { key: 'economic_buyer', dbKey: 'economic_buyer_score', label: 'Economic Buyer', icon: User, description: 'Decision maker with budget authority' },
  { key: 'decision_criteria', dbKey: 'decision_criteria_score', label: 'Decision Criteria', icon: FileText, description: 'Technical and business requirements' },
  { key: 'decision_process', dbKey: 'decision_process_score', label: 'Decision Process', icon: GitBranch, description: 'Steps, timeline, approvers' },
  { key: 'pain', dbKey: 'identify_pain_score', label: 'Identify Pain', icon: AlertTriangle, description: 'Compelling event and urgency' },
  { key: 'champion', dbKey: 'champion_score', label: 'Champion', icon: Star, description: 'Internal advocate with power' },
  { key: 'competition', dbKey: 'competition_score', label: 'Competition', icon: Swords, description: 'Competitive landscape and position' },
];

function getScoreLevel(score) {
  if (score >= 70) return 'good';
  if (score >= 40) return 'warning';
  return 'critical';
}

function getScoreIcon(score) {
  if (score >= 70) return '✓';
  if (score >= 40) return '⚠';
  return '✗';
}

function MeddpicScorecard({ opportunityId, scores = {}, details = {}, overallScore = 0, onUpdate }) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editScores, setEditScores] = useState({});

  // Build meddicc object from scores and details props
  const meddicc = {
    overall_score: overallScore,
    metrics: { score: scores.metrics || 0, ...details.metrics },
    economic_buyer: { score: scores.economic_buyer || 0, ...details.economic_buyer },
    decision_criteria: { score: scores.decision_criteria || 0, ...details.decision_criteria },
    decision_process: { score: scores.decision_process || 0, ...details.decision_process },
    pain: { score: scores.identify_pain || 0, ...details.identify_pain },
    champion: { score: scores.champion || 0, ...details.champion },
    competition: { score: scores.competition || 0, ...details.competition },
  };

  const updateMutation = useMutation({
    mutationFn: (scoreUpdates) => apiClient.put(`/api/opportunities/${opportunityId}/meddicc`, scoreUpdates),
    onSuccess: () => {
      setIsEditing(false);
      setEditScores({});
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      onUpdate?.();
    },
  });

  const handleStartEdit = () => {
    setEditScores({
      metrics_score: meddicc.metrics?.score || 0,
      economic_buyer_score: meddicc.economic_buyer?.score || 0,
      decision_criteria_score: meddicc.decision_criteria?.score || 0,
      decision_process_score: meddicc.decision_process?.score || 0,
      identify_pain_score: meddicc.pain?.score || 0,
      champion_score: meddicc.champion?.score || 0,
      competition_score: meddicc.competition?.score || 0,
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    updateMutation.mutate(editScores);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditScores({});
  };

  // Generate copy text for Salesforce
  const generateCopyText = () => {
    const lines = [];
    lines.push(`MEDDICC SCORE: ${meddicc.overall_score || 0}/100`);
    lines.push('');

    for (const dim of DIMENSIONS) {
      const data = meddicc[dim.key] || {};
      const score = data.score || 0;
      const icon = getScoreIcon(score);

      lines.push(`${dim.label.toUpperCase()} (${score}%): ${icon}`);

      if (data.contact) {
        lines.push(`- Contact: ${data.contact}${data.title ? ` (${data.title})` : ''}`);
      }
      if (data.notes) {
        lines.push(`- ${data.notes}`);
      }
      lines.push('');
    }

    return lines.join('\n');
  };

  const overallLevel = getScoreLevel(overallScore);

  return (
    <div className="msc-container">
      <div className="msc-header">
        <div className="msc-title-row">
          <h3 className="msc-title">MEDDICC Scorecard</h3>
          <div className={`msc-overall-score msc-score-${overallLevel}`}>
            {overallScore}/100
          </div>
        </div>
        <div className="msc-actions">
          {isEditing ? (
            <>
              <button className="msc-btn msc-btn-cancel" onClick={handleCancel} disabled={updateMutation.isPending}>
                <X size={14} /> Cancel
              </button>
              <button className="msc-btn msc-btn-save" onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 size={14} className="msc-spinner" /> : <Save size={14} />}
                Save
              </button>
            </>
          ) : (
            <>
              <CopyButton
                text={generateCopyText()}
                label="Copy Scorecard"
                showLabel
                className="copy-btn-pill"
              />
              <button className="msc-btn msc-btn-edit" onClick={handleStartEdit}>
                <Pencil size={14} /> Edit
              </button>
            </>
          )}
        </div>
      </div>

      <div className="msc-dimensions">
        {DIMENSIONS.map((dim) => {
          const data = meddicc[dim.key] || {};
          const score = isEditing ? (editScores[dim.dbKey] || 0) : (data.score || 0);
          const level = getScoreLevel(score);
          const Icon = dim.icon;

          return (
            <div key={dim.key} className={`msc-dimension msc-dim-${level}`}>
              <div className="msc-dim-header">
                <div className="msc-dim-label">
                  <Icon size={14} />
                  <span>{dim.label}</span>
                </div>
                <div className="msc-dim-score-display">
                  <span className={`msc-dim-score msc-score-${level}`}>{score}%</span>
                  {!isEditing && (
                    <CopyButton
                      text={`${dim.label}: ${score}%${data.notes ? `\n${data.notes}` : ''}`}
                      size={12}
                    />
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="msc-dim-edit">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={score}
                    onChange={(e) => setEditScores(s => ({ ...s, [dim.dbKey]: parseInt(e.target.value) }))}
                    className="msc-slider"
                  />
                </div>
              ) : (
                <>
                  <div className="msc-bar-track">
                    <div
                      className={`msc-bar-fill msc-bar-${level}`}
                      style={{ width: `${score}%` }}
                    />
                  </div>

                  {(data.contact || data.notes) && (
                    <div className="msc-dim-details">
                      {data.contact && (
                        <div className="msc-dim-contact">
                          <User size={12} />
                          <span>{data.contact}{data.title ? ` — ${data.title}` : ''}</span>
                        </div>
                      )}
                      {data.notes && (
                        <div className="msc-dim-notes">{data.notes}</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Gaps Summary */}
      {!isEditing && (
        <div className="msc-gaps">
          <h4 className="msc-gaps-title">Gaps to Address</h4>
          <ul className="msc-gaps-list">
            {DIMENSIONS.filter(d => (meddicc[d.key]?.score || 0) < 50).map(dim => (
              <li key={dim.key} className="msc-gap-item">
                <span className="msc-gap-dim">{dim.label}</span>
                <span className="msc-gap-desc">{dim.description}</span>
              </li>
            ))}
            {DIMENSIONS.every(d => (meddicc[d.key]?.score || 0) >= 50) && (
              <li className="msc-gap-none">No critical gaps - deal is well-qualified</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MeddpicScorecard;
